import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Menu, X, LogOut, LayoutDashboard, User, Calendar, FileText, CreditCard, Users, Settings, DollarSign, CalendarDays, FileSpreadsheet, BookOpen, FileCog, Folder } from 'lucide-react';

export default function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const employeeNav = [
    { name: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
    { name: 'Profile', icon: User, path: '/profile' },
    { name: 'Attendance', icon: Calendar, path: '/attendance' },
    { name: 'Leave Ledger', icon: BookOpen, path: '/leave-ledger' },
    { name: 'Documents', icon: FileText, path: '/documents' },
    { name: 'Payslips', icon: CreditCard, path: '/payslips' },
    { name: 'Policies', icon: FileCog, path: '/policies' },
  ];

  const adminNav = [
    { name: 'Dashboard', icon: LayoutDashboard, path: '/admin/dashboard' },
    { name: 'Employees', icon: Users, path: '/admin/employees' },
    { name: 'Attendance', icon: Calendar, path: '/admin/attendance' },
    { name: 'Attendance Summary', icon: FileSpreadsheet, path: '/admin/attendance-summary' },
    { name: 'Documents', icon: Folder, path: '/admin/documents' },
    { name: 'Salary Rules', icon: Settings, path: '/admin/salary-rules' },
    { name: 'Process Salary', icon: DollarSign, path: '/admin/process-salary' },
    { name: 'Holidays', icon: CalendarDays, path: '/admin/holidays' },
    { name: 'Policies', icon: FileCog, path: '/admin/policies' },
  ];

  const navigation = user.role === 'admin' ? adminNav : employeeNav;

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile header */}
      <div className="lg:hidden bg-white/80 backdrop-blur-md border-b border-slate-200/50 px-4 py-3 flex items-center justify-between">
        <h1 className="text-xl font-bold">Digitopper HR</h1>
        <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="mobile-menu-button">
          {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </Button>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`
          fixed lg:static inset-y-0 left-0 z-50 w-64 bg-primary text-primary-foreground
          transform transition-transform duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}>
          <div className="flex flex-col h-full">
            <div className="p-6 border-b border-slate-700">
              <h1 className="text-2xl font-bold">Digitopper HR</h1>
              <p className="text-sm text-slate-300 mt-1">{user.role === 'admin' ? 'Admin Panel' : 'Employee Portal'}</p>
            </div>

            <nav className="flex-1 p-4 space-y-2">
              {navigation.map((item) => {
                const Icon = item.icon;
                const isActive = window.location.pathname === item.path;
                return (
                  <button
                    key={item.path}
                    onClick={() => {
                      navigate(item.path);
                      setSidebarOpen(false);
                    }}
                    className={`
                      w-full flex items-center space-x-3 px-4 py-3 rounded-lg
                      transition-colors duration-200
                      ${isActive 
                        ? 'bg-accent text-accent-foreground' 
                        : 'hover:bg-slate-700 text-slate-200'
                      }
                    `}
                    data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                  >
                    <Icon className="h-5 w-5" />
                    <span className="font-medium">{item.name}</span>
                  </button>
                );
              })}
            </nav>

            <div className="p-4 border-t border-slate-700">
              <div className="mb-4 p-3 bg-slate-800 rounded-lg">
                <p className="text-sm font-medium truncate">{user.full_name || 'User'}</p>
                <p className="text-xs text-slate-400 truncate">{user.email}</p>
              </div>
              <Button 
                variant="outline" 
                className="w-full border-slate-600 hover:bg-slate-700" 
                onClick={handleLogout}
                data-testid="logout-button"
              >
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </Button>
            </div>
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 min-h-screen">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {children}
          </div>
        </main>
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
}
